import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem, RotateCcw, Users } from 'lucide-react'
import useOpenTeam from '../../../hooks/useOpenTeam'
import AnimatedNumber from '../../../components/AnimatedNumber'
import { buildTeamColorMap, buildTeamsSignature } from '../utils/teamColors'

const CONTAINER_VARIANT = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }
const ROW_VARIANT = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
}

/**
 * Сводка банд раунда: строка на каждую банду с 2+ участниками (`is_team: true`).
 * Суммарный процент банды — это её шанс на победу, поэтому он читается как у
 * одиночки: цифра справа + фоновая полоса доли банды в банке.
 *
 * Банда с `is_team: false` — обычный игрок с бейджем, отдельной строки не
 * получает. Если командных банд в раунде нет (или ключа `teams` нет вовсе) —
 * компонент не рендерится.
 *
 * Цвет строки берётся из общей палитры (`utils/teamColors`) — тот же, которым
 * покрашены плитки банды в treemap-сводке участников и полоса её группы в ленте
 * ставок.
 */
const RoyaleTeamsSummary = ({ teams = [] }) => {
  const { t } = useTranslation()
  const openTeam = useOpenTeam()
  const teamsSignature = buildTeamsSignature(teams)

  const teamColors = useMemo(() => buildTeamColorMap(teams), [teamsSignature])
  const rows = useMemo(
    () => (teams || [])
      .filter((team) => team?.is_team)
      .sort((a, b) => Number(b?.percent || 0) - Number(a?.percent || 0)),
    [teamsSignature]
  )

  if (rows.length === 0) return null

  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 font-semibold text-white">
          <Users className="h-4 w-4 text-team-accent" />
          {t('royale.teams.title')}
        </h2>
        <span className="hidden inline-flex items-center gap-1 rounded-full border border-info/30 bg-info/10 px-2 py-0.5 text-[10px] font-semibold text-info">
          <RotateCcw className="h-3 w-3" />
          {t('royale.teams.refundHint')}
        </span>
      </div>

      <motion.div
        className="space-y-2"
        variants={CONTAINER_VARIANT}
        initial="hidden"
        animate="visible"
      >
        {rows.map((team) => {
          const color = teamColors.get(team.slug) || '#A78BFA'
          const percent = Number(team.percent || 0)
          const refund = Number(team.refund || 0)

          return (
            <motion.div
              key={team.slug}
              variants={ROW_VARIANT}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-dark-primary/60 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
            >
              {/* Доля банды в банке — фоновая полоса цветом банды */}
              <div
                className="pointer-events-none absolute inset-y-0 left-0 transition-[width] duration-500"
                style={{
                  width: `${Math.min(100, Math.max(0, percent))}%`,
                  background: `linear-gradient(90deg, ${color}2E, ${color}00)`
                }}
              />

              <div className="relative flex items-center gap-3">
                <img
                  src={team.ava || `https://ui-avatars.com/api/?name=${encodeURIComponent(team.name || 'Team')}&background=A78BFA&color=0A0A0F`}
                  alt={team.name}
                  className="h-10 w-10 shrink-0 rounded-xl border-2 object-cover"
                  style={{ borderColor: color }}
                  loading="lazy"
                />

                <div className="min-w-0 flex-1">
                  <p
                    onClick={team.slug ? (event) => openTeam(event, team.slug) : undefined}
                    className={`truncate text-sm font-semibold text-white ${
                      team.slug ? 'cursor-pointer transition-transform active:scale-[0.98]' : ''
                    }`}
                    title={team.name}
                  >
                    {team.name}
                  </p>
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-text-secondary">
                    <span className="inline-flex items-center gap-1 tabular-nums">
                      <Users className="h-3 w-3" />
                      {Number(team.count_users || 0)}
                    </span>
                    <span className="inline-flex items-center gap-1 tabular-nums">
                      <Gem className="h-3 w-3 text-accent-primary" />
                      <AnimatedNumber value={Number(team.total_crystal || 0)} />
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="text-lg font-bold tabular-nums text-white">
                    {percent.toFixed(2)}%
                  </span>
                  {refund > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-info/30 bg-info/10 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-info">
                      <RotateCcw className="h-3 w-3" />
                      {refund.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  )
}

export default RoyaleTeamsSummary
